import { inject, Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { PlatformService } from '@core/services/platform.service';

/**
 * Preloads only the routes a visitor is actually likely to open next, and only once
 * the browser is idle.
 *
 * The previous `PreloadAllModules` pulled *every* lazy chunk right after bootstrap —
 * including the ~1.9 MB CKEditor bundle behind the forum/support editor and the whole
 * member area. Anonymous visitors paid that download (and its parse cost) on every
 * first visit without ever navigating there.
 *
 * A route opts in with `data: { preload: true }`; everything else is fetched on demand.
 * Preloading is additionally skipped when the client signals a constrained connection
 * (Save-Data or 2g/3g), where speculative downloads cost more than they save.
 */
@Injectable({ providedIn: 'root' })
export class SelectivePreloadStrategy implements PreloadingStrategy {

  private readonly platform = inject(PlatformService);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!route.data?.['preload'] || !this.platform.isBrowser || this.saveData()) {
      return of(null);
    }
    // let the initial render, hydration and above-the-fold work settle first
    return timer(2000).pipe(mergeMap(() => this.whenIdle().pipe(mergeMap(() => load()))));
  }

  /** Respects the user's data-saver preference and slow connections. */
  private saveData(): boolean {
    const connection = (navigator as any).connection;
    if (!connection) { return false; }
    return !!connection.saveData || /(^|\W)(slow-)?2g$/.test(connection.effectiveType ?? '');
  }

  private whenIdle(): Observable<unknown> {
    const ric = (window as any).requestIdleCallback;
    if (typeof ric !== 'function') { return of(null); }
    return new Observable(subscriber => {
      const handle = ric(() => { subscriber.next(null); subscriber.complete(); }, { timeout: 5000 });
      return () => (window as any).cancelIdleCallback?.(handle);
    });
  }
}
